/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import Button from 'relements/build/esm/ui/button';
import Accordion from 'relements/build/esm/ui/accordion';
import Icon from 'relements/build/esm/ui/icon';
import Drawer from 'relements/build/esm/overlays/drawer';
import Loader from 'relements/build/esm/ui/loader';
import WithTooltip from 'relements/build/esm/overlays/withTooltip';

import Tour from 'reactour';

import { getAllTemplatesForSidebar } from 'actions/TemplateActions';
import { MIX_PANEL } from '@src/const';

// Stores
import TemplateStore from '@src/stores/TemplateStore';
import BotStore from '@src/store';

import ButtonV2 from '@src/components/ButtonV2';
import EmptyCard from '@src/components/EmptyCard';
import InfoCard from '@src/components/InfoCard';
import Substory from './components/Substory';
import Node from './components/Node';

import PlusIcon from './assets/PlusIcon.svg';
import CopyIcon from './assets/Copy.svg';
import NoSubstories from './assets/NoSubstories.svg';
import CancelIcon from './assets/CancelIcon.svg';
import SmartSkill from './assets/SmartSkill.svg';

import styles from './SubstoryNavigation.scss';

const SubstoryNavigation = ({
  substories,
  nodes,
  active,
  prefixComponent,
  postfixComponent,
  onNodeDragStart,
  onNodeDragEnd,
  onNodeDrop,
  onSubstoryChange,
  onSubstoryRename,
  onNodeChange,
  activeNodeId,
  className,
  draggable,
  prefixComponentCustomCss,
  prefixSubstoryComponent,
  postfixSubstoryComponent,
  loading,
  newNodeSidebarEnabled,
  enablePasteSkill,
  resetSubStoryCopy,
  handlePasteSkill,
  highlightSkill,
  addSkillDrawerActive,
  openAddSkillDrawer,
  closeAddSkillDrawer,
  domain,
  smallTalkNav,
}) => {
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const [templateListCalled, setTemplateListCalled] = React.useState(false);
  const [isInfoCardActive, setIsInfoCardActive] = React.useState(true);
  const [loadingNav, setIsLoadingNav] = React.useState(loading);
  const [contentLoaded, setContentLoaded] = React.useState(false);
  const activeSubstories = substories.filter((subStory) => subStory.active);
  const inactiveSubstories = substories.filter((subStory) => !subStory.active);
  const isTourEnabled = JSON.parse(localStorage.getItem('isTourEnabled'));
  const [stepsEnabled, setStepEnabled] = React.useState(isTourEnabled);
  const [startingStep, setStartingStep] = React.useState(0);
  const hasSelfServeLayout = BotStore.getSelfServeLayout();
  const [addSkillsDrawerLoaded, setAddSkillsDrawerLoaded] = React.useState(
    false
  );
  const domainRef = React.useRef();
  const mixPanelTracker = BotStore.getMixPanel();

  const toggleActiveIndex = React.useCallback(
    (i, substory) => () => {
      const isSame = i === activeIndex;
      if (!isSame) onSubstoryChange(substory);
      setActiveIndex(isSame ? -1 : i);
    },
    [activeIndex, onSubstoryChange]
  );

  const handleDragStart = React.useCallback(
    (substory) => (step, i) => (e) => {
      setActiveIndex(-1);
      onNodeDragStart(substory)(step, i)(e);
    },
    [onNodeDragStart]
  );

  React.useEffect(() => {
    if (!contentLoaded) {
      setIsLoadingNav(true);
      const templateList = TemplateStore.getAllTemplates();
      if (templateList.length === 0 && !templateListCalled) {
        setTemplateListCalled(true);
        setTimeout(() => {
          getAllTemplatesForSidebar(
            {
              active: true,
              full: false,
            },
            () => {
              setContentLoaded(true);
              setIsLoadingNav(false);
            }
          );
        }, 50);
      } else {
        setIsLoadingNav(false);
      }
    } else {
      setIsLoadingNav(false);
    }

    const tourEnabled = isTourEnabled
      ? !loadingNav && isTourEnabled
      : isTourEnabled;
    setStepEnabled(tourEnabled);
  }, [contentLoaded, isTourEnabled, loadingNav, templateListCalled]);

  React.useEffect(() => {
    if (hasSelfServeLayout && domainRef.current !== domain?.id) {
      setActiveIndex(-1);
      domainRef.current = domain?.id;
    }
  }, [domain, hasSelfServeLayout]);

  const renderPasteSkill = (onEmptyState) => {
    return (
      <div
        onClick={() => {
          closeAddSkillDrawer();
          handlePasteSkill();
        }}
        className={`${styles.substoryNavigationSectionLabel} ${
          onEmptyState && styles.substoryNavigationSectionLabelPasteEmpty
        } ${styles.substoryNavigationSectionLabelPaste}`}
      >
        <div
          className={styles.substoryNavigationSectionLabelPasteTextContainer}
        >
          <Icon
            src={CopyIcon}
            className={styles.substoryNavigationSectionLabelIcon}
          />
          <span className={styles.substoryNavigationSectionLabelPasteText}>
            Paste Skill
          </span>
        </div>

        <WithTooltip tooltip="Don't Paste" tooltipPosition="BOTTOM">
          <Icon
            src={CancelIcon}
            className={styles.substoryNavigationSectionLabelIcon}
            onClick={(e) => {
              resetSubStoryCopy(e);
            }}
          />
        </WithTooltip>
      </div>
    );
  };

  const closeTour = () => {
    let tourStepEvent = '';
    if (startingStep === 0) {
      tourStepEvent = MIX_PANEL.SMART_SKILLS.TOUR_CANCELLED_STEP_1;
    } else if (startingStep === 1) {
      tourStepEvent = MIX_PANEL.SMART_SKILLS.TOUR_CANCELLED_STEP_2;
    } else if (startingStep === 2) {
      tourStepEvent = MIX_PANEL.SMART_SKILLS.TOUR_COMPLETED;
    }

    mixPanelTracker.track({
      event: tourStepEvent,
      payload: { 'Bot Name': domain.title },
    });

    setStartingStep(0);
    localStorage.setItem('isTourEnabled', JSON.stringify(false));
    localStorage.setItem('userDisabledTour', JSON.stringify(true));
    setStepEnabled(false);
    closeAddSkillDrawer(false);
  };

  const updateStartingStep = React.useCallback(() => {
    setStartingStep(1);
    setStepEnabled(true);
    setAddSkillsDrawerLoaded(true);
  }, []);

  const mixPanelNextStep = () => {
    let tourStepEvent = '';
    if (startingStep === 0) {
      tourStepEvent = MIX_PANEL.SMART_SKILLS.TOUR_STEP_1_NEXT;
    } else if (startingStep === 1) {
      tourStepEvent = MIX_PANEL.SMART_SKILLS.TOUR_STEP_2_NEXT;
    }
    mixPanelTracker.track({
      event: tourStepEvent,
      payload: { 'Bot Name': domain.title },
    });
  };

  const handleStepChange = (currentStep) => {
    if (currentStep === 1 && !addSkillsDrawerLoaded) {
      openAddSkillDrawer(false);
      setStepEnabled(false);
      setTimeout(() => {
        updateStartingStep();
      }, 320);
    }
    setStartingStep(currentStep);
  };

  const allSteps = [
    {
      selector: '.test',
      content: (
        <div className={styles.substoryNavigationIntro}>
          <Icon
            src="closeV2"
            onClick={() => {
              closeTour();
            }}
            className={styles.substoryNavigationIntroClose}
          />
          <div className={styles.substoryNavigationIntroText}>
            A skill is a conversational journey designed to handle a set of user
            queries.
          </div>
          <div className={styles.substoryNavigationIntroText}>
            There are two types of skills:
          </div>
          <InfoCard
            isActive
            icon={SmartSkill}
            noMargin
            lateral
            noClose
            title="Smart Skills"
            subtext={
              <div>
                These are highly intelligent, pre-built conversational journeys.
              </div>
            }
          />
          <br />
          <InfoCard
            isActive
            noMargin
            lateral
            noClose
            danger
            title="Custom Skills"
            subtext={
              <div>
                These are custom journeys built by you to solve user queries.
              </div>
            }
          />
        </div>
      ),
      placement: 'right-end',
      disableBeacon: true,
    },

    {
      selector: '.test2',
      content: (
        <div className={styles.substoryNavigationIntro}>
          <Icon
            src="closeV2"
            onClick={() => {
              closeTour();
            }}
            className={`introjs-closebutton ${styles.substoryNavigationIntroClose}`}
          />
          <div className={styles.substoryNavigationIntroText}>
            Search and add your preferred Smart Skill from the below verticals
            in one click. For eg, &quot;Claim Insurance&quot;
          </div>
        </div>
      ),
      placement: 'right',
    },

    {
      selector: '.test3',
      content: (
        <div className={styles.substoryNavigationIntro}>
          <Icon
            src="closeV2"
            onClick={() => {
              closeTour();
            }}
            className={`introjs-closebutton ${styles.substoryNavigationIntroClose}`}
          />
          <div className={styles.substoryNavigationIntroText}>
            You can create your own Custom Skill if you don’t find a SmartSkill
            matching your requirements.
          </div>
        </div>
      ),
      placement: 'right',
    },
  ];

  return (
    <>
      {!hasSelfServeLayout && (
        <Tour
          steps={allSteps}
          isOpen={stepsEnabled && !loading && active}
          startAt={startingStep}
          onRequestClose={closeTour}
          disableKeyboardNavigation
          disableInteraction
          showNumber={false}
          closeWithMask={false}
          highlightedMaskClassName="substoryNavigationTourHighlight"
          getCurrentStep={(curr) => handleStepChange(curr)}
          lastStepNextButton={
            <Button
              themed
              variant="secondary"
              className="substoryNavigationTourActions"
            >
              Done
            </Button>
          }
          nextButton={
            <Button
              themed
              variant="secondary"
              className="substoryNavigationTourActions"
              onClick={() => mixPanelNextStep()}
            >
              Next
            </Button>
          }
          prevButton={
            <Button
              themed
              variant="secondary"
              className="substoryNavigationTourActions"
            >
              Back
            </Button>
          }
          className="substoryNavigationTour"
        />
      )}

      <Drawer
        active={active}
        position="LEFT"
        width={hasSelfServeLayout ? 318 : 300}
        zIndex={4}
        noOverlay
        noDisable
        offset={hasSelfServeLayout ? 24 : 0}
        attachTo="app-body"
        prefixClassName={`substoryNavigationDrawer${
          hasSelfServeLayout ? '_ww' : ''
        }`}
      >
        {prefixComponent}
        {substories.length > 0 || newNodeSidebarEnabled || smallTalkNav ? (
          <div
            className={`${styles.substoryNavigation} ${className} ${
              enablePasteSkill && styles.substoryNavigationPaste
            }`}
          >
            <Accordion useDragHandle>
              {substories.length ? (
                <div className={styles.substoryNavigationSectionLabel}>
                  Skills
                  <ButtonV2
                    type="text"
                    onClick={openAddSkillDrawer}
                    disabled={addSkillDrawerActive}
                  >
                    <Icon
                      src={PlusIcon}
                      className={styles.substoryNavigationSectionLabelIcon}
                    />
                    <div
                      className={styles.substoryNavigationSectionLabelButton}
                    >
                      Add Skill
                    </div>
                  </ButtonV2>
                </div>
              ) : null}

              {/* Paste Skill */}
              {enablePasteSkill && renderPasteSkill()}
              {activeSubstories.map((substory, i) => {
                const subStoryProps = {
                  smartSkillsEnabled: true,
                  disabled: false,
                  onToggle: toggleActiveIndex(i, substory),
                  active: activeIndex === i,
                  activeNodeId,
                  index: i,
                  id: substory.id,
                  nodes: substory.steps,
                  color: substory.color,
                  title: substory.title,
                  contextMenu: substory.contextMenu,
                  onNodeDrop: onNodeDrop(substory),
                  onNodeDragEnd,
                  onNodeDragStart: handleDragStart(substory),
                  onNodeChange,
                  onSubstoryRename: (newName) =>
                    onSubstoryRename(newName, substory),
                  draggable,
                  prefixComponent: prefixSubstoryComponent,
                  postfixComponent: postfixSubstoryComponent,
                  highlightSkill:
                    highlightSkill === substory.id &&
                    activeSubstories.length > 1,
                  closeAddSkillDrawer,
                  isSmartSkill: substory.smart_skill,
                };

                return <Substory {...subStoryProps} />;
              })}

              {inactiveSubstories.map((substory, i) => {
                const index = activeSubstories.length + i;
                const disabledSubStoryProps = {
                  smartSkillsEnabled: true,
                  disabled: true,
                  onToggle: toggleActiveIndex(index, substory),
                  active: activeIndex === index,
                  activeNodeId,
                  index,
                  id: substory.id,
                  nodes: substory.steps,
                  color: substory.color,
                  title: substory.title,
                  contextMenu: substory.contextMenu,
                  onNodeDrop: onNodeDrop(substory),
                  onNodeDragEnd,
                  onNodeDragStart: handleDragStart(substory),
                  onNodeChange,
                  onSubstoryRename: (newName) =>
                    onSubstoryRename(newName, substory),
                  draggable,
                  highlightSkill: false,
                  closeAddSkillDrawer,
                };

                return <Substory {...disabledSubStoryProps} />;
              })}
            </Accordion>
            {nodes.map((node, i) => {
              return (
                <Node
                  key={i}
                  type={node.type}
                  color={node.color}
                  title={node.title}
                  contextMenu={node.contextMenu}
                  active={node.id === activeNodeId}
                  disabled={!node.active}
                  onClick={() => onNodeChange(node)}
                  customCSS={prefixComponentCustomCss}
                  draggable={false}
                />
              );
            })}
          </div>
        ) : (
          <>
            {/* Paste Skill */}
            {enablePasteSkill && renderPasteSkill(true)}
            <div
              className={`${styles.substoryNavigationSectionEmpty} ${
                enablePasteSkill &&
                styles.substoryNavigationSectionEmptyTopPasteEnabled
              } ${styles.substoryNavigationSectionEmptyTop}`}
            >
              <div className="test">
                <EmptyCard
                  icon={NoSubstories}
                  largeIcon
                  noBorder
                  subtext={
                    <div className={styles.substoryNavigationSectionEmptyText}>
                      Start building your bot by adding
                      <a
                        href="https://docs.haptik.ai/smart-skills/what-are-smart-skills"
                        target="_blank"
                        className={styles.substoryNavigationSectionLink}
                        style={{
                          display: 'initial',
                        }}
                        rel="noreferrer"
                      >
                        {` skills `}
                      </a>
                      to it!
                    </div>
                  }
                  customCTA={
                    <ButtonV2
                      type="text"
                      prefixClassName={styles.substoryNavigationSectionEmptyCTA}
                      onClick={openAddSkillDrawer}
                      disabled={addSkillDrawerActive}
                    >
                      <Icon
                        src={PlusIcon}
                        className={styles.substoryNavigationSectionLabelIcon}
                      />
                      <div
                        className={styles.substoryNavigationSectionLabelButton}
                      >
                        Add Skill
                      </div>
                    </ButtonV2>
                  }
                />
              </div>
            </div>
            <div
              className={`${styles.substoryNavigationSectionEmpty} ${
                BotStore.getSelfServeLayout()
                  ? styles.substoryNavigationSectionEmptyBottom_ww
                  : styles.substoryNavigationSectionEmptyBottom
              }`}
            >
              <InfoCard
                isActive={isInfoCardActive}
                onDismiss={() => {
                  setIsInfoCardActive(false);
                }}
                newFeature
                icon={SmartSkill}
                title="Smart Skills"
                subtext={
                  <div>
                    Smart Skills are industry specific conversational journeys
                    pre-built by experts to build an IVA with minimum efforts.
                    <a
                      href="https://docs.haptik.ai/smart-skills/what-are-smart-skills"
                      target="_blank"
                      className={styles.substoryNavigationSectionLink}
                      rel="noreferrer"
                    >
                      Know More
                    </a>
                  </div>
                }
              />
            </div>
          </>
        )}

        {loadingNav && (
          <div className={styles.substoryNavigationSectionLoading}>
            <Loader />
          </div>
        )}

        {((substories.length > 0 && !loadingNav) ||
          newNodeSidebarEnabled ||
          smallTalkNav) &&
          postfixComponent}
      </Drawer>
    </>
  );
};

SubstoryNavigation.propTypes = {
  active: PropTypes.bool,
  activeNodeId: PropTypes.string,
  activeSubstoryId: PropTypes.string,
  className: PropTypes.string,
  draggable: PropTypes.bool,
  nodes: PropTypes.arrayOf(PropTypes.shape({})),
  onNodeChange: PropTypes.func,
  onNodeDragEnd: PropTypes.func,
  onNodeDragStart: PropTypes.func,
  onNodeDrop: PropTypes.func,
  onSubstoryChange: PropTypes.func,
  onSubstoryRename: PropTypes.func,
  postfixComponent: PropTypes.node,
  postfixSubstoryComponent: PropTypes.node,
  prefixComponentCustomCss: PropTypes.bool,
  prefixComponent: PropTypes.node,
  prefixSubstoryComponent: PropTypes.node,
  substories: PropTypes.arrayOf(PropTypes.shape({})),
};

SubstoryNavigation.defaultProps = {
  active: false,
  draggable: true,
  activeNodeId: '',
  className: '',
  activeSubstoryId: '',
  onNodeChange: () => null,
  onNodeDragEnd: () => null,
  onNodeDragStart: () => null,
  onNodeDrop: () => null,
  onSubstoryChange: () => null,
  onSubstoryRename: () => null,
  postfixComponent: null,
  prefixComponent: null,
  prefixComponentCustomCss: false,
  prefixSubstoryComponent: null,
  postfixSubstoryComponent: null,
  substories: [],
  nodes: [],
};

export default React.memo(SubstoryNavigation, (prevProps, nextProps) => {
  if (prevProps.substories.length !== nextProps.substories.length) return false;
  if (prevProps.activeNodeId !== nextProps.activeNodeId) return false;
  if (prevProps.active !== nextProps.active) return false;
  if (prevProps.loading !== nextProps.loading) return false;
  if (prevProps.addSkillDrawerActive !== nextProps.addSkillDrawerActive)
    return false;

  let prevFlatNodesActive = prevProps.nodes.filter((node) => node.active);
  let nextFlatNodesActive = nextProps.nodes.filter((node) => node.active);

  const prevFlatSubstoriesActive = prevProps.substories.filter((subStory) => {
    prevFlatNodesActive = prevFlatNodesActive.concat(
      subStory.nodeList.filter((node) => node.active)
    );
    return subStory.active;
  });

  const nextFlatSubstoriesActive = nextProps.substories.filter((subStory) => {
    nextFlatNodesActive = nextFlatNodesActive.concat(
      subStory.nodeList.filter((node) => node.active)
    );
    return subStory.active;
  });

  if (prevFlatSubstoriesActive.length !== nextFlatSubstoriesActive.length)
    return false;

  if (prevFlatNodesActive.length !== nextFlatNodesActive.length) return false;
  if (prevProps.shouldUpdate !== nextProps.shouldUpdate) return false;

  return true;
});
